import { z } from "zod";
import { SITE_ORIGIN, UTM_REGISTRY, isTrackablePath, sanitizePageUrl, hostMatches } from "./analytics";
import { CONSENT_STORAGE_KEY, CONSENT_VERSION, parseStoredConsent, type ConsentChoice } from "./consent";

export const ATTRIBUTION_KEY = "seoevo_agency_attribution";
export const ATTRIBUTION_AGE_MS = 90 * 86400000;
const sourceValues = [...UTM_REGISTRY.utm_source, "direct", "unknown"] as const;
const canonical = z.string().max(500).refine((v) => {
  try {
    const u = new URL(v);
    return u.origin === SITE_ORIGIN && isTrackablePath(u.pathname) && sanitizePageUrl(v, { allowUtm: false }) === v;
  } catch { return false; }
});
export const touchSchema = z.object({
  source: z.enum(sourceValues),
  medium: z.enum(UTM_REGISTRY.utm_medium).nullable(),
  campaign: z.enum(UTM_REGISTRY.utm_campaign).nullable(),
  content: z.enum(UTM_REGISTRY.utm_content).nullable(),
  landing: canonical,
  // Closed referrer origins: arbitrary hostnames can themselves contain personal data.
  referrer: z.enum([SITE_ORIGIN, "https://www.google.com", "https://www.bing.com", "https://www.linkedin.com", "https://www.instagram.com"]).nullable(),
  at: z.number().int().nonnegative(),
}).strict();
export const attributionSchema = z.object({
  v: z.literal(1),
  consent: z.object({ v: z.literal(CONSENT_VERSION), analytics: z.literal(true), ts: z.number().int().nonnegative() }).strict(),
  first: touchSchema,
  last_non_direct: touchSchema.nullable(),
  expires_at: z.number().int(),
}).strict();
export type Attribution = z.infer<typeof attributionSchema>;
type Storage = Pick<globalThis.Storage, "getItem" | "setItem" | "removeItem">;

export function validateAttribution(value: unknown, now = Date.now()): Attribution | null {
  const p = attributionSchema.safeParse(value);
  if (!p.success) return null;
  const a = p.data;
  if (a.consent.ts > now || now - a.consent.ts > 180 * 86400000 ||
      a.first.at < a.consent.ts || a.first.at > now || a.expires_at !== a.first.at + ATTRIBUTION_AGE_MS || now >= a.expires_at ||
      (a.last_non_direct && (a.last_non_direct.source === "direct" || a.last_non_direct.at < a.first.at || a.last_non_direct.at > now))) return null;
  return a;
}

export function clearAttribution(storage: Pick<Storage, "removeItem">) {
  try { storage.removeItem(ATTRIBUTION_KEY); } catch { /* fail closed: no reads */ }
}

export function attributionChoiceValid(choice: ConsentChoice | null, now: number): boolean {
  return !!choice?.analytics && !!parseStoredConsent(JSON.stringify(choice), now);
}

export function readAttribution(storage: Storage, choice: ConsentChoice | null, now = Date.now()): Attribution | null {
  if (!attributionChoiceValid(choice, now)) { clearAttribution(storage); return null; }
  try {
    const a = validateAttribution(JSON.parse(storage.getItem(ATTRIBUTION_KEY) ?? "null"), now);
    // A new consent decision must not resurrect a previous consent period.
    if (!a || a.consent.ts !== choice?.ts) { clearAttribution(storage); return null; }
    return a;
  } catch { clearAttribution(storage); return null; }
}

export function captureAttribution(storage: Storage, choice: ConsentChoice | null, href: string, referrer: string, now = Date.now()): Attribution | null {
  if (!attributionChoiceValid(choice, now) || !choice) { clearAttribution(storage); return null; }
  let u: URL;
  try { u = new URL(href, SITE_ORIGIN); } catch { return null; }
  if (!isTrackablePath(u.pathname)) return null;
  const safe = new URL(sanitizePageUrl(href, { allowUtm: true }) ?? SITE_ORIGIN);
  let ref: z.infer<typeof touchSchema>["referrer"] = null;
  let source: z.infer<typeof touchSchema>["source"] = referrer ? "unknown" : "direct";
  try {
    const r = new URL(referrer);
    if (hostMatches(r.hostname, "agenciaseoevolution.com")) { ref = SITE_ORIGIN; source = "direct"; }
    else for (const [domain, s, origin] of [
      ["google.com", "google", "https://www.google.com"], ["bing.com", "bing", "https://www.bing.com"],
      ["linkedin.com", "linkedin", "https://www.linkedin.com"], ["instagram.com", "instagram", "https://www.instagram.com"],
    ] as const) { if (hostMatches(r.hostname, domain)) { source = s; ref = origin; break; } }
  } catch { /* absent / unrecognised referrer */ }
  const touch = touchSchema.parse({
    source: safe.searchParams.get("utm_source") ?? source,
    medium: safe.searchParams.get("utm_medium"), campaign: safe.searchParams.get("utm_campaign"), content: safe.searchParams.get("utm_content"),
    landing: sanitizePageUrl(href, { allowUtm: false }), referrer: ref, at: now,
  });
  const prev = readAttribution(storage, choice, now);
  const next: Attribution = prev ? { ...prev, last_non_direct: touch.source !== "direct" ? touch : prev.last_non_direct } : {
    v: 1, consent: { v: CONSENT_VERSION, analytics: true, ts: choice.ts }, first: touch,
    last_non_direct: touch.source !== "direct" ? touch : null, expires_at: now + ATTRIBUTION_AGE_MS,
  };
  try { storage.setItem(ATTRIBUTION_KEY, JSON.stringify(next)); return readAttribution(storage, choice, now); }
  catch { clearAttribution(storage); return null; }
}

// Always revalidate the persisted consent before marketing reads; runtime alone may be stale.
export function currentBrowserAttribution(runtimeChoice: ConsentChoice | null): Attribution | null {
  try {
    const choice = parseStoredConsent(window.localStorage.getItem(CONSENT_STORAGE_KEY), Date.now());
    if (!runtimeChoice?.analytics || !choice?.analytics || runtimeChoice.ts !== choice.ts) {
      clearAttribution(window.localStorage); return null;
    }
    return readAttribution(window.localStorage, choice);
  } catch { return null; }
}